import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';

import store from '../../redux/store';
import { showMultiBoardLessonState } from '../../redux/actions';
import classApi from '../../Api/classApi';
import GenericState from '../../MyComponents/GenericLoadingErrorState/GenericState';
import GenericDeletionConfirmation from '../../MyComponents/GenericDeletionConfirmation/GenericDeletionConfirmation';
import DeleteIcon from '../../MyComponents/icons/DeleteIcon';
import { canIncreaseLimitedData, CAPABILITIES, getIntPermission } from '../../shared/Capabilities';
import { SUBSCRIPTIONS_LINK } from '../../common/urlLinks';
import { CARD, KICKER, PILL_PRIMARY, PILL_SECONDARY, cx } from '../Home/HomeDashboard/dashboardStyles';
import { COLORS2, REDESIGN_COLORS, REDESIGN_FONTS } from '../../constants';

// Below this the boards count folds under the date.
const NARROW = '@media (max-width: 700px)';

const useStyles = makeStyles(() => ({
    title: {
        margin: '0 0 14px',
        fontSize: '1.2rem',
        letterSpacing: '-0.02em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
    },
    // The plan's saved-session limit is reached: an amber notice with the
    // way out.
    notice: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px 20px',
        margin: '0 0 16px',
        padding: '14px 18px',
        border: `1px solid ${REDESIGN_COLORS.amberBorder}`,
        borderRadius: '16px',
        background: REDESIGN_COLORS.amberSurface,
        color: REDESIGN_COLORS.dark,
        fontSize: '0.92rem',
        fontWeight: REDESIGN_FONTS.weightStrong,
    },
    noticeCta: {
        ...PILL_SECONDARY,
        minHeight: '40px',
        padding: '0 16px',
        fontSize: '0.88rem',
    },
    list: {
        ...CARD,
        overflow: 'hidden',
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1.6fr) auto minmax(0, 1fr) auto',
        alignItems: 'center',
        gap: '16px',
        padding: '14px 20px',
        [NARROW]: {
            gridTemplateColumns: 'minmax(0, 1fr) auto',
            gap: '10px 12px',
            padding: '14px 16px',
        },
    },
    head: {
        borderBottom: `1px solid ${REDESIGN_COLORS.sand}`,
        background: REDESIGN_COLORS.cream,
        [NARROW]: {
            display: 'none',
        },
    },
    headCell: KICKER,
    row: {
        borderBottom: `1px solid ${REDESIGN_COLORS.sand}`,
        '&:last-child': {
            borderBottom: 0,
        },
        '&:hover': {
            background: REDESIGN_COLORS.cream,
        },
    },
    // A session past the plan's limit, as the old table tinted it.
    locked: {
        background: REDESIGN_COLORS.amberSurface,
        '&:hover': {
            background: REDESIGN_COLORS.amberSurface,
        },
    },
    date: {
        margin: 0,
        fontSize: '1rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
    },
    time: {
        display: 'inline-block',
        padding: '4px 10px',
        borderRadius: '999px',
        background: REDESIGN_COLORS.mist,
        color: REDESIGN_COLORS.link,
        fontSize: '0.8rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        whiteSpace: 'nowrap',
    },
    count: {
        color: REDESIGN_COLORS.body,
        fontSize: '0.92rem',
        [NARROW]: {
            gridColumn: '1 / -1',
            marginTop: '-4px',
        },
    },
    actions: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: '8px',
        [NARROW]: {
            gridColumn: '1 / -1',
        },
    },
    view: {
        ...PILL_PRIMARY,
        minHeight: '40px',
        padding: '0 16px',
        fontSize: '0.88rem',
    },
    remove: {
        display: 'grid',
        placeItems: 'center',
        width: '36px',
        height: '36px',
        padding: 0,
        border: `1px solid ${REDESIGN_COLORS.line}`,
        borderRadius: '50%',
        color: COLORS2.virtualRed,
        background: REDESIGN_COLORS.white,
        cursor: 'pointer',
        '&:hover': {
            borderColor: COLORS2.virtualRed,
        },
        '& svg': {
            width: '18px',
            height: '18px',
        },
    },
    empty: {
        ...CARD,
        padding: '22px 24px',
        background: REDESIGN_COLORS.mist,
        borderColor: REDESIGN_COLORS.primaryBorderSoft,
        color: REDESIGN_COLORS.body,
        fontSize: '0.95rem',
    },
}));

const DATE_FORMAT = { year: 'numeric', month: 'long', day: 'numeric' };
const TIME_FORMAT = { hour: 'numeric', minute: 'numeric' };

/**
 * The saved individual-boards sessions of a group, newest as the server
 * returns them: date, time, how many boards, and view/delete. Owners see the
 * count and can delete; the plan's session limit tints the rows past it.
 */
const MultiBoardTable = ({ lessonId, lessonName, profile, displayTitle = true, subscriptions, isOwner = true }) => {
    const classes = useStyles();
    const intl = useIntl();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [deleting, setDeleting] = useState(null);
    const [canCreate, setCanCreate] = useState(null);

    const load = () =>
        classApi
            .getMultiBoards(lessonId)
            .then((res) => {
                setData(res.data.multiBoards);
                setLoading(false);
            })
            .catch((err) => {
                console.error(err);
                setLoading(false);
                setError(true);
            });

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lessonId]);

    useEffect(() => {
        if (subscriptions && profile) {
            canIncreaseLimitedData(
                subscriptions,
                profile.subscription,
                CAPABILITIES.GROUP_COUNT,
                Promise.resolve(data.length)
            ).then((res) => setCanCreate(res));
        } else {
            setCanCreate({ value: true });
        }
    }, [subscriptions, profile, data]);

    if (loading || error) {
        return <GenericState isLoading={loading} isError={error} inline={true} />;
    }

    const view = (row) =>
        store.dispatch(
            showMultiBoardLessonState({ id: row.id, date: +row.ts, lessonId, name: lessonName, email: profile.email, isOwner })
        );

    const remove = () => {
        const id = deleting;
        setDeleting(null);
        setLoading(true);
        classApi
            .deleteMultiBoards(lessonId, id)
            .catch((err) => console.error('unable to delete entire multiboard', lessonId, id, err))
            .then(load);
    };

    let limit = 0;
    if (subscriptions && profile && profile.subscription) {
        limit = getIntPermission(subscriptions, profile.subscription, CAPABILITIES.INDIVIDUAL_SESSION_COUNT).value;
    }
    const lockedAt = (index) => isOwner && index >= limit;

    return (
        <>
            {displayTitle ? (
                <h3 className={classes.title}>
                    <FormattedMessage id="menu.pastLessons" />
                </h3>
            ) : null}

            {canCreate && canCreate.value === false && isOwner ? (
                <div className={classes.notice} role="status">
                    <span>
                        <FormattedMessage id="subscription.upsell.multiBoard.exceeded" />
                    </span>
                    {!window.isTeams ? (
                        <Link to={SUBSCRIPTIONS_LINK} className={classes.noticeCta}>
                            <FormattedMessage id="redesign.cta.plans" />
                            <span aria-hidden="true">→</span>
                        </Link>
                    ) : null}
                </div>
            ) : null}

            {data.length === 0 ? (
                <div className={classes.empty}>
                    <FormattedMessage id="multiBoard.noBoards" />
                </div>
            ) : (
                <div className={classes.list} role="table">
                    <div className={cx(classes.grid, classes.head)} role="row">
                        <span className={classes.headCell} role="columnheader">
                            <FormattedMessage id="multiBoard.dateLabel" />
                        </span>
                        <span />
                        <span className={classes.headCell} role="columnheader">
                            {isOwner ? <FormattedMessage id="multiBoard.participantCountLabel" /> : null}
                        </span>
                        <span />
                    </div>
                    {data.map((row, index) => {
                        const when = new Date(row.createdAt);
                        return (
                            <div
                                key={row.id}
                                className={cx(classes.grid, classes.row, lockedAt(index) && classes.locked)}
                                role="row"
                            >
                                <p className={classes.date} role="cell">
                                    {intl.formatDate(when, DATE_FORMAT)}
                                </p>
                                <span className={classes.time} role="cell">
                                    {intl.formatTime(when, TIME_FORMAT)}
                                </span>
                                <span className={classes.count} role="cell">
                                    {isOwner ? (
                                        <>
                                            <FormattedMessage id="multiBoard.participantCountLabel" />: {row.userCount}
                                        </>
                                    ) : null}
                                </span>
                                <div className={classes.actions} role="cell">
                                    <button type="button" onClick={() => view(row)} className={classes.view}>
                                        <FormattedMessage id="redesign.generic.label.view" />
                                        <span aria-hidden="true">→</span>
                                    </button>
                                    {isOwner ? (
                                        <button
                                            type="button"
                                            onClick={() => setDeleting(row.id)}
                                            aria-label={intl.formatMessage({ id: 'generic.label.delete' })}
                                            className={classes.remove}
                                        >
                                            <DeleteIcon />
                                        </button>
                                    ) : null}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <GenericDeletionConfirmation
                open={!!deleting}
                deleteFunction={remove}
                onClose={() => setDeleting(null)}
                type={intl.formatMessage({ id: 'redesign.updateClass.activity' }).toLowerCase()}
            />
        </>
    );
};

const mapStateToProps = (state) => {
    const { profile, mainAppState } = state;
    return { profile, subscriptions: mainAppState.subscriptions };
};

export default connect(mapStateToProps)(MultiBoardTable);
